import { isEmpty } from 'lodash';
import { ReactNode, useEffect, useState } from 'react';
import { Button } from '@shared/components/Button';
import { Icon } from '@shared/components/Icon';
import { Text } from '@shared/components/Text';
import { ScrollView, View } from '@shared/components/View';
import { FilterTagOption } from '@shared/components/filter/types';
import { MBTextInput } from '@shared/components/textInput';
import { Colors, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import styles from './MobileMultiSelect.module.css';

interface MobileMultiSelectProps {
  options: FilterTagOption[];
  selectedOptions?: FilterTagOption[];
  onChange(options: FilterTagOption[]): void;
  onSearch?(keyword: string): void;
  onDeselectAll(): void;
  renderItem: (props: { data: FilterTagOption }) => ReactNode;
  paddingTop?: number;
  paddingHorizontal?: number;
  isMultiSelect?: boolean;
}

interface OptionsListItemProps {
  item: FilterTagOption;
  renderItem: (props: { data: FilterTagOption }) => ReactNode;
  isSelected?: boolean;
  showCheckbox?: boolean;
  onChange: () => void;
}

function OptionsListItem({
  item,
  renderItem,
  isSelected = false,
  showCheckbox = true,
  onChange,
}: OptionsListItemProps) {
  return (
    <View
      key={item.value}
      row
      fullWidth
      paddingVertical={1.2}
      role="checkbox"
      aria-checked={isSelected}
      onClick={onChange}
      style={{ justifyContent: 'space-between' }}
    >
      <View>{renderItem({ data: item })}</View>
      {showCheckbox && (
        <View>
          <Icon
            name={isSelected ? 'check-box' : 'check-box-empty'}
            color={Colors.v2.green}
            gridSize={5}
          />
        </View>
      )}
    </View>
  );
}

export function MobileMultiSelect({
  onChange,
  onSearch,
  onDeselectAll,
  options,
  selectedOptions,
  renderItem,
  paddingTop = grid(2),
  paddingHorizontal = grid(2),
  isMultiSelect = true,
}: MobileMultiSelectProps) {
  const [keyword, setKeyword] = useState('');
  const [keywordFilteredOptions, setKeywordFilteredOptions] = useState(options);
  const [keywordFilteredSelections, setKeywordFilteredSelections] = useState<
    FilterTagOption[] | undefined
  >(selectedOptions);
  const selectionsList = keywordFilteredSelections;
  const optionsList = (onSearch ? options : keywordFilteredOptions).filter(
    o => !selectionsList?.find(s => s.value === o.value),
  );

  useEffect(() => {
    setKeywordFilteredSelections(
      selectedOptions?.filter(option => option.label.toLowerCase().includes(keyword.toLowerCase())),
    );
    setKeywordFilteredOptions(
      options?.filter(option => option.label.toLowerCase().includes(keyword.toLowerCase())),
    );
  }, [keyword, selectedOptions, options]);

  const isFiltered = !isEmpty(keyword);

  return (
    <View>
      <View
        className={styles.searchRow}
        vars={{ 'search-padding-top': paddingTop, 'search-padding-horizontal': paddingHorizontal }}
      >
        <MBTextInput
          autoFocus
          placeholder={i18n('common.searchDotDotDot')}
          onChangeText={text => {
            setKeyword(text);

            if (onSearch) {
              onSearch(text);
            }
          }}
        />
      </View>
      <ScrollView paddingVertical={2} paddingHorizontal={2}>
        {!isFiltered && !isEmpty(selectionsList) && (
          <View
            row
            centerVertically
            style={{ justifyContent: 'space-between' }}
            paddingLeft={1}
            marginBottom={1}
          >
            <View>
              <Text size="body2">
                <Text size="body2" weight="semibold">
                  {selectionsList?.length ?? 0}{' '}
                </Text>
                Selected
              </Text>
            </View>
            <View>
              <Button variant="ghost" size="small" onClick={onDeselectAll}>
                <View row centerVertically>
                  <Text size="body2">Deselect All</Text>
                  <View marginLeft={1}>
                    <Icon name="check-box" color={Colors.v2.green} gridSize={5} />
                  </View>
                </View>
              </Button>
            </View>
          </View>
        )}
        {selectionsList?.map(item => (
          <OptionsListItem
            key={item.value}
            isSelected
            onChange={() => {
              if (isMultiSelect) {
                onChange(selectedOptions?.filter(o => o.value !== item.value) ?? []);
              } else {
                onChange([]);
              }
            }}
            item={item}
            renderItem={renderItem}
          />
        ))}

        {!isFiltered && !isEmpty(optionsList) && (
          <View marginVertical={1} paddingHorizontal={1}>
            <Text size="overline">Filter By</Text>
          </View>
        )}

        {optionsList.map(item => (
          <OptionsListItem
            key={item.value}
            onChange={() => {
              if (isMultiSelect) {
                onChange([...(selectedOptions || []), item]);
              } else {
                onChange([item]);
              }
            }}
            item={item}
            renderItem={renderItem}
          />
        ))}
      </ScrollView>
    </View>
  );
}
